import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { cbotUsdT, miles } from "./lib";
import CbotStrip from "./CbotStrip";
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, Clock, Target } from "lucide-react";

const VENTA_TIPO = "Espejo";
const fmtValM = (n) => {
  n = Number(n) || 0;
  if (Math.abs(n) >= 1e6) return `USD ${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  return `USD ${miles(n)}`;
};

export default function GranosPosicion({ setTab }) {
  const [raw, setRaw] = useState([]);
  const [cbot, setCbot] = useState(null);
  const [deseados, setDeseados] = useState([]);
  const [margen, setMargen] = useState(2);
  const [loading, setLoading] = useState(true);

  const loadCbot = useCallback(() => api.get("/granos/cbot").then((r) => setCbot(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d, s] = await Promise.all([
        api.get("/granos/contratos"),
        api.get("/granos/precio-deseado").catch(() => ({ data: { items: [] } })),
        api.get("/granos/notif-settings").catch(() => ({ data: {} })),
      ]);
      setRaw(c.data.contratos || []);
      setDeseados(d.data.items || []);
      if (s?.data?.margen_pct != null) setMargen(s.data.margen_pct);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    loadCbot();
    const id = setInterval(loadCbot, 60000);
    return () => clearInterval(id);
  }, [load, loadCbot]);

  const pos = useMemo(() => {
    let compras = 0, ventas = 0, sinCP = 0, valHoy = 0, valCtr = 0, hasCbot = false;
    const items = cbot?.items || [];
    for (const c of raw) {
      const pend = Number(c.pendiente) || 0;
      if (c.tipo_contrato === VENTA_TIPO) ventas += pend; else compras += pend;
      sinCP += Number(c.sin_costo_precio) || 0;
      const chi = cbotUsdT(c.cultivo || c.producto, items);
      if (chi != null && pend > 0) { hasCbot = true; valHoy += pend * (chi + (Number(c.costo) || 0)); valCtr += pend * (Number(c.precio) || 0); }
    }
    return { compras, ventas, neto: compras - ventas, sinCP, valHoy, diff: valHoy - valCtr, hasCbot };
  }, [raw, cbot]);

  const alertas = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dias = (fe) => (fe ? Math.round((new Date(String(fe).slice(0, 10)) - today) / 86400000) : null);
    const atrasados = raw.filter((c) => c.estado === "Atrasado" && (c.pendiente || 0) > 0)
      .map((c) => ({ ...c, d: c.dias_atraso ?? Math.abs(dias(c.fecha_entrega) || 0) }))
      .sort((a, b) => (b.d || 0) - (a.d || 0));
    const vence = raw.filter((c) => {
      if (c.estado === "Atrasado" || (c.pendiente || 0) <= 0 || !c.fecha_entrega) return false;
      const dd = dias(c.fecha_entrega); return dd >= 0 && dd <= 7;
    }).map((c) => ({ ...c, d: dias(c.fecha_entrega) }));
    const items = cbot?.items || [];
    const precio = [];
    for (const dd of deseados) {
      const chi = cbotUsdT(dd.cultivo, items);
      const pf = (+dd.precio_usd_tn || 0) - (+dd.costo_entrega_usd_tn || 0);
      if (chi == null || !pf) continue;
      const abiertas = raw.filter((x) => (x.productor || x.cliente) === dd.cliente && (x.cultivo || x.producto) === dd.cultivo)
        .reduce((s, x) => s + (Number(x.pendiente) || 0), 0);
      if (abiertas <= 0) continue;
      let est = null;
      if (chi >= pf) est = "ALCANZADO";
      else if (chi >= pf * (1 - margen / 100)) est = "CERCA";
      if (est) precio.push({ ...dd, chi, abiertas, est });
    }
    return { atrasados, vence, precio };
  }, [raw, cbot, deseados, margen]);

  const up = pos.diff >= 0;

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0f5233]" /></div>;

  return (
    <div className="p-4 space-y-4" data-testid="granos-posicion-tab">
      <CbotStrip />

      <div className="rounded-2xl border bg-white p-4">
        <p className="text-sm font-semibold text-slate-500">Posición abierta (neto)</p>
        <p className="text-3xl font-extrabold text-[#14532d] mt-1" data-testid="granos-posicion-neto">{pos.neto >= 0 ? "+ " : "− "}{miles(Math.abs(pos.neto))} t</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-500">Compras pendientes</p><p className="font-bold text-slate-800">{miles(pos.compras)} t</p></div>
          <div className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-500">Ventas pendientes</p><p className="font-bold text-slate-800">{miles(pos.ventas)} t</p></div>
        </div>
        {pos.sinCP > 0 && <p className="mt-2 text-xs font-semibold text-amber-700">{miles(pos.sinCP)} t con precio sin fijar</p>}
      </div>

      <div className="rounded-2xl border bg-white p-4">
        <p className="text-sm font-semibold text-slate-500">Valor a precio de hoy</p>
        {pos.hasCbot ? (
          <>
            <p className="text-2xl font-extrabold text-slate-900 mt-1">{fmtValM(pos.valHoy)}</p>
            <div className="mt-2 flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2">
              <span className="text-xs font-semibold text-[#14532d]">Resultado vs. precio contratado</span>
              <span className={`font-extrabold flex items-center gap-1 ${up ? "text-emerald-700" : "text-red-700"}`}>
                {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />} USD {miles(Math.abs(pos.diff))}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-slate-400">Chicago + base del contrato, por cada tonelada pendiente.</p>
          </>
        ) : <p className="mt-1 text-sm font-semibold text-amber-700">Sin precio de Chicago disponible.</p>}
      </div>

      <p className="font-extrabold text-slate-900">Alertas</p>
      {alertas.atrasados.map((c, i) => (
        <AlertRow key={`a${i}`} tag="ATRASADO" color="red" icon={AlertTriangle}
          title={c.productor || c.cliente} sub={`Ctr ${c.contrato} · ${miles(c.pendiente)} t pend. · ${c.d || 0} días`} />
      ))}
      {alertas.vence.map((c, i) => (
        <AlertRow key={`v${i}`} tag="VENCE" color="amber" icon={Clock}
          title={c.productor || c.cliente} sub={`Vence en ${c.d} días · ${miles(c.pendiente)} t pend.`} />
      ))}
      {alertas.precio.map((a, i) => (
        <AlertRow key={`p${i}`} tag="PRECIO" color="blue" icon={Target} onClick={() => setTab && setTab("precio")}
          title={`${a.cultivo} ${a.est === "ALCANZADO" ? "llegó a tu precio objetivo" : "cerca de tu precio objetivo"}`}
          sub={`${a.cliente} · fijá precio en ${miles(a.abiertas)} t abiertas`} />
      ))}
      {alertas.atrasados.length + alertas.vence.length + alertas.precio.length === 0 && (
        <p className="text-center text-sm text-slate-400 py-6">Sin alertas.</p>
      )}
    </div>
  );
}

const COLORMAP = {
  red: ["border-red-200", "bg-red-600"],
  amber: ["border-amber-200", "bg-amber-600"],
  blue: ["border-blue-200", "bg-blue-600"],
};
function AlertRow({ tag, color, icon: Icon, title, sub, onClick }) {
  const [border, badge] = COLORMAP[color];
  return (
    <button onClick={onClick} data-testid={`granos-posicion-alert-${tag.toLowerCase()}`}
      className={`w-full text-left flex items-center gap-3 rounded-2xl border-2 ${border} bg-white p-3 ${onClick ? "active:bg-slate-50" : ""}`}>
      <span className={`shrink-0 rounded-lg ${badge} text-white text-[11px] font-extrabold px-2 py-1 flex items-center gap-1`}><Icon className="h-3.5 w-3.5" />{tag}</span>
      <span className="min-w-0">
        <span className="block font-extrabold text-slate-900 text-sm truncate">{title}</span>
        <span className="block text-xs text-slate-500">{sub}</span>
      </span>
    </button>
  );
}
